import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";


export default function SaveProgressIndicator({
  active = false,
  progress = null,
  accentColor = "#dba529",
}) {
  const phase = progress?.phase || (active ? "saving" : "idle");
  const percent =
    typeof progress?.percent === "number" && Number.isFinite(progress.percent)
      ? Math.min(100, Math.max(0, progress.percent))
      : null;
  const current = Number(progress?.current) || 0;
  const total = Number(progress?.total) || 0;
  const hasFiles = total > 0;
  const isDeterminate =
    phase === "uploading" && percent != null && percent < 100;

  if (!active) return null;

  const countLabel =
    hasFiles && phase === "uploading"
      ? `Uploading ${Math.min(current || 1, total)} of ${total}`
      : hasFiles && phase === "processing"
        ? `Processing ${total} file${total === 1 ? "" : "s"}`
        : null;

  return (
    <Box
      sx={{
        width: "100%",
        mb: 1.5,
        px: 0.25,
        "@keyframes savePulseDot": {
          "0%, 100%": { opacity: 0.35, transform: "scale(0.85)" },
          "50%": { opacity: 1, transform: "scale(1)" },
        },
        "@keyframes saveCloudBob": {
          "0%, 100%": { transform: "translateY(0)" },
          "50%": { transform: "translateY(-4px)" },
        },
        "@keyframes saveShimmer": {
          "0%": { transform: "translateX(-120%)" },
          "100%": { transform: "translateX(220%)" },
        },
        "@keyframes saveDotWave": {
          "0%, 80%, 100%": { opacity: 0.25, transform: "translateY(0)" },
          "40%": { opacity: 1, transform: "translateY(-2px)" },
        },
      }}
      aria-live="polite"
      aria-busy="true"
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 0.75 }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              background: `${accentColor}18`,
              border: `1px solid ${accentColor}44`,
              animation: "saveCloudBob 1.4s ease-in-out infinite",
            }}
          >
            <CloudUploadOutlinedIcon
              sx={{ fontSize: 16, color: accentColor }}
            />
          </Box>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  flexShrink: 0,
                  background: accentColor,
                  animation: "savePulseDot 1.2s ease-in-out infinite",
                }}
              />
            {countLabel ? (
              <Typography
                sx={{
                  fontSize: 12.5,
                  color: "rgba(255,255,255,0.72)",
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {countLabel}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={0.35} sx={{ flexShrink: 0 }}>
                {[0, 1, 2].map((index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 3.5,
                      height: 3.5,
                      borderRadius: "50%",
                      background: accentColor,
                      animation: "saveDotWave 1.2s ease-in-out infinite",
                      animationDelay: `${index * 0.18}s`,
                    }}
                  />
                ))}
              </Stack>
            </Stack>
        </Stack>

        <Typography
          sx={{
            fontSize: 11.5,
            color: accentColor,
            fontWeight: 600,
            flexShrink: 0,
            opacity: 0.95,
          }}
        >
          {isDeterminate ? `${percent}%` : null}
        </Typography>
      </Stack>

      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: 999,
        }}
      >
        <LinearProgress
          variant={isDeterminate ? "determinate" : "indeterminate"}
          value={isDeterminate ? percent : undefined}
          sx={{
            height: 4,
            borderRadius: 999,
            backgroundColor: "rgba(255,255,255,0.08)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 999,
              background: accentColor,
            },
          }}
        />
        <Box
          sx={{
            pointerEvents: "none",
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.28), transparent)",
            animation: "saveShimmer 1.8s ease-in-out infinite",
          }}
        />
      </Box>
    </Box>
  );
}
